/**
 * Visualización dual UM en circuito de venta:
 * - Normal: Cantidad + UM = base
 * - Extra Ped/Fac solo si hay snapshot y UM snapshot ≠ UM base
 */

export type UmSnapshotKind = 'ped' | 'fac';

export type UmDocumentoLinea = {
  cantidad?: number | null;
  um?: string | null;
  umped?: string | null;
  cantidadUmped?: number | null;
  equivUmped?: number | null;
  precioUnitarioUmped?: number | null;
  umfac?: string | null;
  cantidadUmfac?: number | null;
  equivUmfac?: number | null;
  precioUnitarioUmfac?: number | null;
  precioUnitario?: number | null;
};

const norm = (v?: string | null) => String(v ?? '').trim();

const umKey = (v?: string | null) => norm(v).toUpperCase();

export function cantidadBase(linea: UmDocumentoLinea): number {
  const n = Number(linea.cantidad);
  return Number.isFinite(n) ? n : 0;
}

export function umBase(linea: UmDocumentoLinea): string {
  return norm(linea.um);
}

export function cantidadDoc(linea: UmDocumentoLinea, kind: UmSnapshotKind): number | undefined {
  const raw = kind === 'ped' ? linea.cantidadUmped : linea.cantidadUmfac;
  if (raw == null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function umDoc(linea: UmDocumentoLinea, kind: UmSnapshotKind): string {
  return kind === 'ped' ? norm(linea.umped) : norm(linea.umfac);
}

export function precioDoc(linea: UmDocumentoLinea, kind: UmSnapshotKind): number | undefined {
  const raw = kind === 'ped' ? linea.precioUnitarioUmped : linea.precioUnitarioUmfac;
  if (raw == null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** Columnas extra Ped/Fac solo si snapshot presente y UM distinta de la base. */
export function showExtraPedFac(linea: UmDocumentoLinea, kind: UmSnapshotKind): boolean {
  const docUm = umDoc(linea, kind);
  const baseUm = umBase(linea);
  if (!docUm) return false;
  const cant = cantidadDoc(linea, kind);
  if (cant == null) return false;
  if (!baseUm) return true;
  return umKey(docUm) !== umKey(baseUm);
}

export function labelsExtra(kind: UmSnapshotKind): { cant: string; um: string } {
  return kind === 'ped'
    ? { cant: 'Cant. Ped', um: 'UM Ped' }
    : { cant: 'Cant. Fac', um: 'UM Fac' };
}

/** Payload de ingreso al copiar/facturar/remitear: preferir snapshot documental. */
export function cantidadUmIngresoFromSnapshot(
  linea: UmDocumentoLinea,
  kind: UmSnapshotKind
): { cantidad: number; um: string; precioUnitario?: number } {
  if (showExtraPedFac(linea, kind) || (umDoc(linea, kind) && cantidadDoc(linea, kind) != null)) {
    const cant = cantidadDoc(linea, kind);
    const um = umDoc(linea, kind);
    if (cant != null && um) {
      const p = precioDoc(linea, kind);
      return {
        cantidad: cant,
        um,
        ...(p != null ? { precioUnitario: p } : {}),
      };
    }
  }
  return {
    cantidad: cantidadBase(linea),
    um: umBase(linea),
    ...(linea.precioUnitario != null && Number.isFinite(Number(linea.precioUnitario))
      ? { precioUnitario: Number(linea.precioUnitario) }
      : {}),
  };
}

type SnapshotRaw = Record<string, unknown>;

const pickStr = (...vals: unknown[]): string | undefined => {
  for (const v of vals) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return undefined;
};

const pickPositiveNum = (...vals: unknown[]): number | undefined => {
  for (const v of vals) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
};

const pickFiniteNum = (...vals: unknown[]): number | undefined => {
  for (const v of vals) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};

/** Snapshot Ped tipado sin `null` (compatible con DTOs de servicios de venta). */
export type UmSnapshotPedNormalized = {
  umped?: string;
  cantidadUmped?: number;
  equivUmped?: number;
  precioUnitarioUmped?: number;
};

/** Snapshot Fac tipado sin `null` (compatible con DTOs de servicios de venta). */
export type UmSnapshotFacNormalized = {
  umfac?: string;
  cantidadUmfac?: number;
  equivUmfac?: number;
  precioUnitarioUmfac?: number;
};

/** Normaliza snapshot Ped desde respuesta API (camelCase / PascalCase). */
export function normalizeUmSnapshotPedFromApi(raw: unknown): UmSnapshotPedNormalized {
  const row = (raw ?? {}) as SnapshotRaw;
  return {
    umped: pickStr(row.umped, row.umPed, row.Umped, row.UMPed),
    cantidadUmped: pickPositiveNum(row.cantidadUmped, row.CantidadUmped, row.CantidadUMPed),
    equivUmped: pickFiniteNum(row.equivUmped, row.EquivUmped, row.EquivUMPed),
    precioUnitarioUmped: pickFiniteNum(
      row.precioUnitarioUmped,
      row.PrecioUnitarioUmped,
      row.PrecioUnitarioUMPed,
    ),
  };
}

/** Normaliza snapshot Fac desde respuesta API (camelCase / PascalCase). */
export function normalizeUmSnapshotFacFromApi(raw: unknown): UmSnapshotFacNormalized {
  const row = (raw ?? {}) as SnapshotRaw;
  return {
    umfac: pickStr(row.umfac, row.umFac, row.Umfac, row.UMFac),
    cantidadUmfac: pickPositiveNum(row.cantidadUmfac, row.CantidadUmfac, row.CantidadUMFac),
    equivUmfac: pickFiniteNum(row.equivUmfac, row.EquivUmfac, row.EquivUMFac),
    precioUnitarioUmfac: pickFiniteNum(
      row.precioUnitarioUmfac,
      row.PrecioUnitarioUmfac,
      row.PrecioUnitarioUMFac,
    ),
  };
}
